<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-6">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p class="text-lg text-gray-600">Last updated: {{ new Date().toLocaleDateString() }}</p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-lg shadow-sm p-8">
        <!-- Introduction -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            At guest5of5, we respect your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains how we collect, use, and safeguard your data when you use our hotel booking platform.
          </p>
          <p class="text-gray-700 leading-relaxed">
            By using our services, you agree to the collection and use of information as described in this policy.
          </p>
        </section>

        <!-- Information We Collect -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
          
          <div class="space-y-4">
            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Personal Information</h3>
              <ul class="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Name, email address, and phone number</li>
                <li>Billing and payment information</li>
                <li>Travel preferences and booking history</li>
                <li>Identification documents for verification</li>
              </ul>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Usage Information</h3>
              <ul class="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Search queries and preferences</li>
              </ul>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Cookies</h3>
              <p class="text-gray-700 mb-2">
                We use cookies to enhance your experience and collect usage data.
              </p>
              <ul class="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Session cookies for authentication</li>
                <li>Analytics cookies for performance monitoring</li>
                <li>Preference cookies for personalization</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- How We Use Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="space-y-3">
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Service Provision</h4>
                  <p class="text-gray-600 text-sm">Process bookings, payments, and provide support</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Communication</h4>
                  <p class="text-gray-600 text-sm">Send booking confirmations and updates</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Personalization</h4>
                  <p class="text-gray-600 text-sm">Customize your experience based on preferences</p>
                </div>
              </div>
            </div>
            
            <div class="space-y-3">
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Security</h4>
                  <p class="text-gray-600 text-sm">Protect against fraud and ensure account security</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Analytics</h4>
                  <p class="text-gray-600 text-sm">Improve our services and understand user behavior</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Legal Compliance</h4>
                  <p class="text-gray-600 text-sm">Meet legal obligations and regulatory requirements</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Information Sharing -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Information Sharing</h2>
          
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <h3 class="font-medium text-gray-900 mb-2">We Do Not Sell Your Data</h3>
            <p class="text-gray-700">
              We do not sell, trade, or rent your personal information to third parties for marketing purposes.
            </p>
          </div>

          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-3">When We Share Information</h3>
            <div class="space-y-3">
              <div class="flex items-start">
                <div class="w-2 h-2 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Hotel Partners</h4>
                  <p class="text-gray-600 text-sm">Share booking details with hotels to fulfill your reservations</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Service Providers</h4>
                  <p class="text-gray-600 text-sm">Work with trusted partners for payment processing and support</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-2 h-2 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 class="font-medium text-gray-900">Legal Requirements</h4>
                  <p class="text-gray-600 text-sm">Comply with laws, regulations, or legal processes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Data Security -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div class="flex items-start">
                <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i class="bi bi-shield-check text-blue-600"></i>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Encryption</h4>
                  <p class="text-gray-600 text-sm">All data is encrypted using industry-standard SSL/TLS protocols</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i class="bi bi-lock text-green-600"></i>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Access Control</h4>
                  <p class="text-gray-600 text-sm">Strict access controls and authentication measures</p>
                </div>
              </div>
            </div>
            
            <div class="space-y-4">
              <div class="flex items-start">
                <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i class="bi bi-eye-slash text-purple-600"></i>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Privacy</h4>
                  <p class="text-gray-600 text-sm">Regular privacy audits and compliance monitoring</p>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i class="bi bi-arrow-repeat text-orange-600"></i>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Backup</h4>
                  <p class="text-gray-600 text-sm">Regular backups and disaster recovery procedures</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Your Rights -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="space-y-3">
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Access your personal data</span>
              </div>
              
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Correct inaccurate information</span>
              </div>
              
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Request deletion of your data</span>
              </div>
            </div>
            
            <div class="space-y-3">
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Object to data processing</span>
              </div>
              
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Receive your data in portable format</span>
              </div>
              
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <i class="bi bi-check text-white text-sm"></i>
                </div>
                <span class="font-medium text-gray-900">Lodge a complaint</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Contact Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
          
          <p class="text-gray-700 mb-6">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          
          <div class="grid md:grid-cols-3 gap-6">
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="bi bi-envelope text-white"></i>
              </div>
              <h4 class="font-medium text-gray-900 mb-1">Email</h4>
              <p class="text-gray-600 text-sm">privacy@guest5of5.com</p>
            </div>
            
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="bi bi-telephone text-white"></i>
              </div>
              <h4 class="font-medium text-gray-900 mb-1">Phone</h4>
              <p class="text-gray-600 text-sm">+1 (555) 123-4567</p>
            </div>
            
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="bi bi-geo-alt text-white"></i>
              </div>
              <h4 class="font-medium text-gray-900 mb-1">Address</h4>
              <p class="text-gray-600 text-sm">123 Business Street<br>New York, NY 10001</p>
            </div>
          </div>
        </section>

        <!-- Footer -->
        <div class="border-t pt-6 mt-8">
          <p class="text-sm text-gray-500 text-center">
            This Privacy Policy is effective as of {{ new Date().toLocaleDateString() }} and will remain in effect except with respect to any changes in its provisions in the future.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      // Add any data properties if needed
    }
  },
  methods: {
    // Add any methods if needed
  }
}
</script>

<style scoped>
/* Add any custom styles if needed */
</style> 